import React, { useState } from 'react';
import { 
	Card, 
	Button, 
	Dropdown, 
	Menu, 
	Modal, 
	Form, 
	Select, 
	Input, 
	Space, 
	Divider,
	Alert,
	Progress,
	List,
	Avatar,
	Tag,
	Tooltip,
	message,
	Popconfirm,
	Badge,
	Checkbox
} from 'antd';
import {
	CheckOutlined,
	DeleteOutlined,
	EditOutlined,
	MailOutlined,
	BankOutlined,
	UserDeleteOutlined,
	ExportOutlined,
	ImportOutlined,
	DownOutlined,
	CloseOutlined,
	SendOutlined,
	StopOutlined,
	CheckCircleOutlined,
	ExclamationCircleOutlined,
	CrownOutlined,
	SafetyOutlined,
	VideoCameraOutlined,
	UserOutlined,
	ReloadOutlined,
	WarningOutlined,
	InfoCircleOutlined,
	FileExcelOutlined,
	FilePdfOutlined,
	FileTextOutlined
} from '@ant-design/icons';
import './BulkActions.scss';

const { Option } = Select;
const { TextArea } = Input;
const { confirm } = Modal;

const BulkActions = ({ 
	selectedUsers = [], 
	onClearSelection, 
	onBulkUpdate,
	onBulkDelete,
	onBulkEmail,
	onBulkExport,
	loading = false 
}) => {
	const [emailModalVisible, setEmailModalVisible] = useState(false);
	const [updateModalVisible, setUpdateModalVisible] = useState(false);
	const [exportModalVisible, setExportModalVisible] = useState(false);
	const [progressModalVisible, setProgressModalVisible] = useState(false);
	const [actionLoading, setActionLoading] = useState(false);
	const [progress, setProgress] = useState(0);
	const [currentAction, setCurrentAction] = useState('');
	const [processedItems, setProcessedItems] = useState([]);
	
	const [emailForm] = Form.useForm();
	const [updateForm] = Form.useForm();
	const [exportForm] = Form.useForm();

	const selectedCount = selectedUsers.length;

	// Email templates
	const emailTemplates = [
		{
			key: 'welcome',
			name: 'Welcome Message',
			subject: 'Welcome to our platform!',
			content: 'Hi {{displayName}},\n\nWelcome to our platform! We\'re excited to have you on board.\n\nBest regards,\nThe Team'
		},
		{
			key: 'verification',
			name: 'Account Verification',
			subject: 'Please verify your account',
			content: 'Hi {{displayName}},\n\nPlease verify your account by clicking the link below.\n\nBest regards,\nThe Team'
		},
		{
			key: 'update',
			name: 'Important Update',
			subject: 'Important platform update',
			content: 'Hi {{displayName}},\n\nWe have an important update to share with you.\n\nBest regards,\nThe Team'
		},
		{
			key: 'custom',
			name: 'Custom Message',
			subject: '',
			content: ''
		}
	];

	// Export formats
	const exportFormats = [
		{ key: 'excel', name: 'Excel (.xlsx)', icon: <FileExcelOutlined />, color: 'green' },
		{ key: 'csv', name: 'CSV (.csv)', icon: <FileTextOutlined />, color: 'blue' },
		{ key: 'pdf', name: 'PDF (.pdf)', icon: <FilePdfOutlined />, color: 'red' },
		{ key: 'json', name: 'JSON (.json)', icon: <FileTextOutlined />, color: 'orange' }
	];

	// Bulk action menu items
	const getBulkActionMenu = () => (
		<Menu onClick={handleMenuClick} className="bulk-actions-menu">
			<Menu.SubMenu 
				key="status" 
				title={<><CheckCircleOutlined /> Change Status</>}
				className="submenu-item"
			>
				<Menu.Item key="activate">
					<CheckCircleOutlined style={{ color: 'var(--success)' }} />
					Activate Users
				</Menu.Item>
				<Menu.Item key="deactivate">
					<StopOutlined style={{ color: 'var(--text-tertiary)' }} />
					Deactivate Users
				</Menu.Item>
				<Menu.Item key="ban">
					<BankOutlined style={{ color: 'var(--accent)' }} />
					Ban Users
				</Menu.Item>
				<Menu.Item key="unban">
					<CheckOutlined style={{ color: 'var(--success)' }} />
					Unban Users
				</Menu.Item>
			</Menu.SubMenu>

			<Menu.SubMenu 
				key="role" 
				title={<><CrownOutlined /> Change Role</>}
				className="submenu-item"
			>
				<Menu.Item key="role-user">
					<UserOutlined /> Set as User
				</Menu.Item>
				<Menu.Item key="role-streamer">
					<VideoCameraOutlined /> Set as Streamer
				</Menu.Item>
				<Menu.Item key="role-moderator">
					<SafetyOutlined /> Set as Moderator
				</Menu.Item>
				<Menu.Item key="role-admin">
					<CrownOutlined /> Set as Admin
				</Menu.Item>
			</Menu.SubMenu>

			<Menu.Divider />

			<Menu.Item key="verify">
				<CheckCircleOutlined style={{ color: 'var(--success)' }} />
				Verify Users
			</Menu.Item>

			<Menu.Item key="unverify">
				<ExclamationCircleOutlined style={{ color: 'var(--warning)' }} />
				Unverify Users
			</Menu.Item>

			<Menu.Divider />

			<Menu.Item key="update">
				<EditOutlined />
				Bulk Update
			</Menu.Item>

			<Menu.Item key="email">
				<MailOutlined />
				Send Email
			</Menu.Item>

			<Menu.Item key="export">
				<ExportOutlined />
				Export Data
			</Menu.Item>

			<Menu.Divider />

			<Menu.Item key="delete" className="danger-item">
				<DeleteOutlined style={{ color: 'var(--accent)' }} />
				Delete Users
			</Menu.Item>
		</Menu>
	);

	// Handle menu click
	const handleMenuClick = ({ key }) => {
		switch (key) {
			case 'activate':
				handleStatusChange('active');
				break;
			case 'deactivate':
				handleStatusChange('inactive');
				break;
			case 'ban':
				handleStatusChange('banned');
				break;
			case 'unban':
				handleStatusChange('active');
				break;
			case 'role-user':
				handleRoleChange('user');
				break;
			case 'role-streamer':
				handleRoleChange('streamer');
				break;
			case 'role-moderator':
				handleRoleChange('moderator');
				break;
			case 'role-admin':
				handleRoleChange('admin');
				break;
			case 'verify':
				handleVerificationChange(true);
				break;
			case 'unverify':
				handleVerificationChange(false);
				break;
			case 'update':
				setUpdateModalVisible(true);
				break;
			case 'email':
				setEmailModalVisible(true);
				break;
			case 'export':
				setExportModalVisible(true);
				break;
			case 'delete':
				handleBulkDelete();
				break;
			default:
				break;
		}
	};

	// Handle status change
	const handleStatusChange = (status) => {
		const statusNames = {
			active: 'activate',
			inactive: 'deactivate',
			banned: 'ban'
		};

		confirm({
			title: `${statusNames[status]} ${selectedCount} user${selectedCount > 1 ? 's' : ''}?`,
			content: `Are you sure you want to ${statusNames[status]} the selected users?`,
			okText: 'Yes',
			okType: status === 'banned' ? 'danger' : 'primary',
			cancelText: 'No',
			onOk() {
				performBulkAction('status', { status }, `${statusNames[status]}d`);
			}
		});
	};

	// Handle role change
	const handleRoleChange = (role) => {
		const roleNames = {
			user: 'User',
			streamer: 'Streamer',
			moderator: 'Moderator',
			admin: 'Admin'
		};

		confirm({
			title: `Change role to ${roleNames[role]}?`,
			content: `Are you sure you want to change the role of ${selectedCount} user${selectedCount > 1 ? 's' : ''} to ${roleNames[role]}?`,
			okText: 'Yes',
			okType: 'primary',
			cancelText: 'No',
			onOk() {
				performBulkAction('role', { role }, `role changed to ${roleNames[role]}`);
			}
		});
	};

	// Handle verification change
	const handleVerificationChange = (verified) => {
		const action = verified ? 'verify' : 'unverify';
		
		confirm({
			title: `${action} ${selectedCount} user${selectedCount > 1 ? 's' : ''}?`,
			content: `Are you sure you want to ${action} the selected users?`,
			okText: 'Yes',
			okType: 'primary',
			cancelText: 'No',
			onOk() {
				performBulkAction('verification', { verified }, `${action}ied`);
			}
		});
	};

	// Handle bulk delete
	const handleBulkDelete = () => {
		confirm({
			title: `Delete ${selectedCount} user${selectedCount > 1 ? 's' : ''}?`,
			content: 'This action cannot be undone. Are you sure you want to delete the selected users?',
			okText: 'Delete',
			okType: 'danger',
			cancelText: 'Cancel',
			onOk() {
				performBulkAction('delete', {}, 'deleted');
			}
		});
	};

	// Perform bulk action with progress
	const performBulkAction = async (actionType, data, successMessage) => {
		setCurrentAction(actionType);
		setProgress(0);
		setProcessedItems([]);
		setProgressModalVisible(true);
		setActionLoading(true);

		try {
			// Simulate processing with progress
			const totalUsers = selectedUsers.length;
			const processed = [];

			for (let i = 0; i < totalUsers; i++) {
				const user = selectedUsers[i];
				const progressPercent = Math.round(((i + 1) / totalUsers) * 100);
				
				// Simulate API call delay
				await new Promise(resolve => setTimeout(resolve, 200));

				try {
					// Call the appropriate handler
					if (actionType === 'delete') {
						await onBulkDelete([user.id]);
					} else {
						await onBulkUpdate([user.id], data);
					}

					processed.push({
						user,
						status: 'success',
						message: `Successfully ${successMessage}`
					});
				} catch (error) {
					processed.push({
						user,
						status: 'error',
						message: error.message || 'Action failed'
					});
				}

				setProgress(progressPercent);
				setProcessedItems([...processed]);
			}

			const successCount = processed.filter(item => item.status === 'success').length;
			const errorCount = processed.filter(item => item.status === 'error').length;

			if (errorCount === 0) {
				message.success(`Successfully ${successMessage} ${successCount} user${successCount > 1 ? 's' : ''}`);
			} else {
				message.warning(`${successCount} user${successCount > 1 ? 's' : ''} ${successMessage}, ${errorCount} failed`);
			}

		} catch (error) {
			message.error('Bulk action failed');
		} finally {
			setActionLoading(false);
		}
	};

	// Handle email send
	const handleEmailSend = async () => {
		try {
			const values = await emailForm.validateFields();
			setCurrentAction('email');
			setProgress(0);
			setProcessedItems([]);
			setProgressModalVisible(true);
			setActionLoading(true);

			const totalUsers = selectedUsers.length;
			const processed = [];

			for (let i = 0; i < totalUsers; i++) {
				const user = selectedUsers[i];
				const progressPercent = Math.round(((i + 1) / totalUsers) * 100);
				
				await new Promise(resolve => setTimeout(resolve, 300));

				try {
					// Replace template variables
					const personalizedSubject = values.subject.replace(/\{\{(\w+)\}\}/g, (match, key) => user[key] || match);
					const personalizedContent = values.content.replace(/\{\{(\w+)\}\}/g, (match, key) => user[key] || match);

					await onBulkEmail([user.id], {
						subject: personalizedSubject,
						content: personalizedContent
					});

					processed.push({
						user,
						status: 'success',
						message: 'Email sent successfully'
					});
				} catch (error) {
					processed.push({
						user,
						status: 'error',
						message: error.message || 'Failed to send email'
					});
				}

				setProgress(progressPercent);
				setProcessedItems([...processed]);
			}

			const successCount = processed.filter(item => item.status === 'success').length;
			const errorCount = processed.filter(item => item.status === 'error').length;

			if (errorCount === 0) {
				message.success(`Email sent to ${successCount} user${successCount > 1 ? 's' : ''}`);
			} else {
				message.warning(`Email sent to ${successCount} user${successCount > 1 ? 's' : ''}, ${errorCount} failed`);
			}

			setEmailModalVisible(false);
			emailForm.resetFields();

		} catch (error) {
			message.error('Failed to send emails');
			setActionLoading(false);
		}
	};

	// Handle bulk update
	const handleBulkUpdate = async () => {
		try {
			const values = await updateForm.validateFields();
			
			// Remove empty values
			const updateData = Object.keys(values).reduce((acc, key) => {
				if (values[key] !== undefined && values[key] !== null && values[key] !== '') {
					acc[key] = values[key];
				}
				return acc;
			}, {});

			if (Object.keys(updateData).length === 0) {
				message.warning('Please select at least one field to update');
				return;
			}

			await performBulkAction('update', updateData, 'updated');
			setUpdateModalVisible(false);
			updateForm.resetFields();

		} catch (error) {
			message.error('Bulk update failed');
		}
	};

	// Handle export
	const handleExport = async () => {
		try {
			const values = await exportForm.validateFields();
			
			setCurrentAction('export');
			setProgress(0);
			setProgressModalVisible(true);
			setActionLoading(true);

			// Simulate export progress
			for (let i = 0; i <= 100; i += 10) {
				await new Promise(resolve => setTimeout(resolve, 200));
				setProgress(i);
			}

			await onBulkExport(selectedUsers.map(user => user.id), values);
			
			message.success(`Successfully exported ${selectedUsers.length} user records`);
			setExportModalVisible(false);
			exportForm.resetFields();

		} catch (error) {
			message.error('Export failed');
		} finally {
			setActionLoading(false);
		}
	};

	// Handle template selection
	const handleTemplateSelect = (templateKey) => {
		const template = emailTemplates.find(t => t.key === templateKey);
		if (template) {
			emailForm.setFieldsValue({
				subject: template.subject,
				content: template.content
			});
		}
	};

	// Close progress modal
	const closeProgressModal = () => {
		setProgressModalVisible(false);
		setProgress(0);
		setProcessedItems([]);
		setCurrentAction('');
	};

	if (selectedCount === 0) {
		return null;
	}

	return (
		<div className="bulk-actions-wrapper">
			<Card className="bulk-actions-card">
				<div className="bulk-actions-content">
					<div className="selection-info">
						<Badge count={selectedCount} className="selection-badge">
							<CheckOutlined className="selection-icon" />
						</Badge>
						<span className="selection-text">
							{selectedCount} user{selectedCount > 1 ? 's' : ''} selected
						</span>
					</div>

					<div className="bulk-actions">
						<Space>
							<Dropdown 
								overlay={getBulkActionMenu()} 
								trigger={['click']}
								placement="bottomRight"
							>
								<Button type="primary" loading={loading}>
									Bulk Actions <DownOutlined />
								</Button>
							</Dropdown>

							<Button 
								icon={<CloseOutlined />}
								onClick={onClearSelection}
								title="Clear selection"
							>
								Clear
							</Button>
						</Space>
					</div>
				</div>
			</Card>

			{/* Email Modal */}
			<Modal
				title={
					<div className="modal-title">
						<MailOutlined className="title-icon" />
						Send Email to {selectedCount} User{selectedCount > 1 ? 's' : ''}
					</div>
				}
				open={emailModalVisible}
				onCancel={() => setEmailModalVisible(false)}
				width={700}
				className="bulk-email-modal"
				footer={[
					<Button key="cancel" onClick={() => setEmailModalVisible(false)}>
						Cancel
					</Button>,
					<Button 
						key="send" 
						type="primary" 
						icon={<SendOutlined />}
						onClick={handleEmailSend}
						loading={actionLoading}
					>
						Send Email
					</Button>
				]}
			>
				<Form form={emailForm} layout="vertical">
					<Form.Item name="template" label="Email Template">
						<Select 
							placeholder="Select a template or create custom"
							onChange={handleTemplateSelect}
							allowClear
						>
							{emailTemplates.map(template => (
								<Option key={template.key} value={template.key}>
									{template.name}
								</Option>
							))}
						</Select>
					</Form.Item>

					<Form.Item 
						name="subject" 
						label="Subject"
						rules={[{ required: true, message: 'Please enter email subject' }]}
					>
						<Input placeholder="Enter email subject" />
					</Form.Item>

					<Form.Item 
						name="content" 
						label="Content"
						rules={[{ required: true, message: 'Please enter email content' }]}
					>
						<TextArea 
							rows={8} 
							placeholder="Enter email content. Use {{username}}, {{displayName}}, {{email}} for personalization"
						/>
					</Form.Item>

					<Alert
						message="Template Variables"
						description="You can use {{username}}, {{displayName}}, {{email}}, {{firstName}}, {{lastName}} in subject and content for personalization."
						type="info"
						showIcon
						className="template-info"
					/>
				</Form>
			</Modal>

			{/* Bulk Update Modal */}
			<Modal
				title={
					<div className="modal-title">
						<EditOutlined className="title-icon" />
						Bulk Update {selectedCount} User{selectedCount > 1 ? 's' : ''}
					</div>
				}
				open={updateModalVisible}
				onCancel={() => setUpdateModalVisible(false)}
				width={600}
				className="bulk-update-modal"
				footer={[
					<Button key="cancel" onClick={() => setUpdateModalVisible(false)}>
						Cancel
					</Button>,
					<Button 
						key="update" 
						type="primary" 
						icon={<CheckOutlined />}
						onClick={handleBulkUpdate}
						loading={actionLoading}
					>
						Update Users
					</Button>
				]}
			>
				<Form form={updateForm} layout="vertical">
					<Alert
						message="Bulk Update"
						description="Only fill in the fields you want to update. Empty fields will be ignored."
						type="info"
						showIcon
						className="update-info"
					/>

					<Form.Item name="status" label="Status">
						<Select placeholder="Select new status" allowClear>
							<Option value="active">Active</Option>
							<Option value="inactive">Inactive</Option>
							<Option value="banned">Banned</Option>
							<Option value="pending">Pending</Option>
						</Select>
					</Form.Item>

					<Form.Item name="role" label="Role">
						<Select placeholder="Select new role" allowClear>
							<Option value="user">User</Option>
							<Option value="streamer">Streamer</Option>
							<Option value="moderator">Moderator</Option>
							<Option value="admin">Admin</Option>
						</Select>
					</Form.Item>

					<Form.Item name="verified" label="Verification Status">
						<Select placeholder="Select verification status" allowClear>
							<Option value={true}>Verified</Option>
							<Option value={false}>Unverified</Option>
						</Select>
					</Form.Item>

					<Form.Item name="country" label="Country">
						<Input placeholder="Enter country" />
					</Form.Item>

					<Form.Item name="language" label="Language">
						<Select placeholder="Select language" allowClear>
							<Option value="en">English</Option>
							<Option value="zh">Chinese</Option>
							<Option value="ja">Japanese</Option>
							<Option value="ko">Korean</Option>
							<Option value="es">Spanish</Option>
							<Option value="fr">French</Option>
						</Select>
					</Form.Item>
				</Form>
			</Modal>

			{/* Export Modal */}
			<Modal
				title={
					<div className="modal-title">
						<ExportOutlined className="title-icon" />
						Export {selectedCount} User{selectedCount > 1 ? 's' : ''}
					</div>
				}
				open={exportModalVisible}
				onCancel={() => setExportModalVisible(false)}
				width={500}
				className="bulk-export-modal"
				footer={[
					<Button key="cancel" onClick={() => setExportModalVisible(false)}>
						Cancel
					</Button>,
					<Button 
						key="export" 
						type="primary" 
						icon={<ExportOutlined />}
						onClick={handleExport}
						loading={actionLoading}
					>
						Export Data
					</Button>
				]}
			>
				<Form form={exportForm} layout="vertical" initialValues={{ format: 'excel' }}>
					<Form.Item 
						name="format" 
						label="Export Format"
						rules={[{ required: true, message: 'Please select export format' }]}
					>
						<Select placeholder="Select export format">
							{exportFormats.map(format => (
								<Option key={format.key} value={format.key}>
									<Space>
										<span style={{ color: `var(--${format.color})` }}>
											{format.icon}
										</span>
										{format.name}
									</Space>
								</Option>
							))}
						</Select>
					</Form.Item>

					<Form.Item name="includeFields" label="Include Fields">
						<Checkbox.Group>
							<div className="checkbox-grid">
								<Checkbox value="basic">Basic Info</Checkbox>
								<Checkbox value="contact">Contact Info</Checkbox>
								<Checkbox value="profile">Profile Data</Checkbox>
								<Checkbox value="settings">Settings</Checkbox>
								<Checkbox value="activity">Activity Data</Checkbox>
								<Checkbox value="statistics">Statistics</Checkbox>
							</div>
						</Checkbox.Group>
					</Form.Item>

					<Form.Item name="filename" label="Filename (optional)">
						<Input placeholder="users_export" />
					</Form.Item>
				</Form>
			</Modal>

			{/* Progress Modal */}
			<Modal
				title={
					<div className="modal-title">
						<ReloadOutlined className="title-icon spinning" />
						Processing {currentAction}...
					</div>
				}
				open={progressModalVisible}
				closable={!actionLoading}
				onCancel={closeProgressModal}
				width={600}
				className="bulk-progress-modal"
				footer={actionLoading ? null : [
					<Button key="close" type="primary" onClick={closeProgressModal}>
						Close
					</Button>
				]}
			>
				<div className="progress-content">
					<Progress 
						percent={progress} 
						status={actionLoading ? 'active' : 'success'}
						strokeColor="var(--text-highlight)"
					/>
					
					<div className="progress-stats">
						<span>Progress: {progress}%</span>
						<span>Processed: {processedItems.length} / {selectedUsers.length}</span>
					</div>

					{processedItems.length > 0 && (
						<div className="processed-items">
							<Divider>Processing Results</Divider>
							<List
								size="small"
								dataSource={processedItems.slice(-5)} // Show last 5 items
								renderItem={item => (
									<List.Item className={`processed-item ${item.status}`}>
										<List.Item.Meta
											avatar={
												<Avatar 
													src={item.user.avatar} 
													icon={<UserOutlined />}
													size="small"
												/>
											}
											title={
												<div className="item-title">
													<span>{item.user.displayName || item.user.username}</span>
													{item.status === 'success' ? (
														<CheckCircleOutlined className="status-icon success" />
													) : (
														<ExclamationCircleOutlined className="status-icon error" />
													)}
												</div>
											}
											description={item.message}
										/>
									</List.Item>
								)}
							/>
							{processedItems.length > 5 && (
								<div className="more-items">
									... and {processedItems.length - 5} more items
								</div>
							)}
						</div>
					)}
				</div>
			</Modal>
		</div>
	);
};

export default BulkActions;
